import {useAuth} from '@apps-common/auth-react';
import DocumentHandler from '@apps-common/auth-utils/DocumentHandler.jsx';
import ErrorPage from '@apps-common/ui-components/ErrorPage.jsx';
import {MapAuthProvider} from '@apps-common/ui-maps/context/MapAuthProvider.jsx';
import {Box, Button, useColorScheme} from '@mui/material';
import {
  ErrorBoundary as SentryErrorBoundary,
  setUser as setSentryUser,
  showReportDialog as showSentryReportDialog,
} from '@sentry/react';
import {useLDClient} from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import React, {Suspense, useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {Navigate, Outlet, Route, Routes, useLocation} from 'react-router';
import Helm from './Helm.jsx';
import {ANALYZE_ROOT_ROUTE} from './constants/routes.ts';
import useAppSection from './hooks/useAppSection.js';
import {useFlags} from './hooks/useFlags.js';
import Analyze from './routes/Analyze.tsx';
import AreasOfInterest from './routes/AreasOfInterest.jsx';
import Overview from './routes/Overview.jsx';
import Search from './routes/Search.jsx';
import LocalSidebar from '@components/LocalSidebar.jsx';
import TopNavBar from '@components/TopNav.jsx';
import {identify, pageEvent} from '@src/analytics.js';
import ModalsManager from '@src/components/dialogs/index.jsx';
import Notifier from '@src/components/notifier/Notifier.tsx';
import {MAPBOX_ACCESS_TOKEN} from '@src/config';

const MapViewer = React.lazy(() => import('./components/MapViewer.jsx'));

const AddNewDestination = React.lazy(
  () => import('./routes/AddNewDestination.jsx'),
);
const Destination = React.lazy(() => import('./routes/Destination.jsx'));
const Destinations = React.lazy(() => import('./routes/Destinations.jsx'));
const Mosaics = React.lazy(() => import('./routes/Mosaics.jsx'));
const Order = React.lazy(() => import('./routes/Order.jsx'));
const Orders = React.lazy(() => import('./routes/Orders.jsx'));
const SavedSearches = React.lazy(() => import('./routes/SavedSearches.jsx'));
const Subscribe = React.lazy(() => import('./routes/Subscribe.jsx'));
const Subscription = React.lazy(() => import('./routes/Subscription.jsx'));
const Subscriptions = React.lazy(() => import('./routes/Subscriptions.jsx'));

const SentryReportButton = ({eventId}) => {
  const {user} = useAuth();

  return (
    <Button
      color="primary"
      variant="contained"
      onClick={() => {
        showSentryReportDialog({
          eventId,
          user: {
            name: user?.claims?.name,
            email: user?.claims?.email,
          },
        });
      }}
    >
      Report issue
    </Button>
  );
};

SentryReportButton.propTypes = {
  eventId: PropTypes.string,
};

const App = () => {
  const auth = useAuth();
  const location = useLocation();
  const ldClient = useLDClient();
  const {setMode} = useColorScheme();

  const {appSection, detailPage} = useAppSection();
  const {showBrowser, showCreateSubscription, showMapRoutes} = useFlags();

  const [isIdentified, setIsIdentified] = useState(false);

  // Force dark mode
  useEffect(() => {
    setMode('dark');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const {
      email,
      organization_id,
      organization_name,
      program_id,
      role_level,
      user_id,
      sh_user_id,
      pl_project,
      pl_workspace,
    } = auth.user?.claims ?? {};

    if (auth.user && !auth.pending && !auth.error) {
      setSentryUser({id: user_id, organization_id, program_id, email});

      identify(auth.user.claims);

      if (user_id && organization_id) {
        ldClient.identify(
          {
            kind: 'multi',
            user: {
              key: `user-${user_id}`,
              email,
              role_level,
              user_id,
              sh_user_id,
              pl_project,
              pl_workspace,
              _meta: {
                privateAttributes: ['email'],
              },
            },
            organization: {
              key: `organization-${organization_id}`,
              organization_id,
              organization_name,
              program_id,
              _meta: {
                privateAttributes: ['organization_name'],
              },
            },
          },
          null,
          () => {
            setIsIdentified(true);
          },
        );
      }
    }
  }, [auth, ldClient]);

  useEffect(() => {
    if (location.pathname !== '/') {
      pageEvent({page: appSection, detailPage});
    }
  }, [location, appSection, detailPage]);

  return (
    <>
      <Helmet
        defaultTitle="Planet Insights Platform"
        titleTemplate="%s ・ Planet Insights Platform"
      />

      <MapAuthProvider
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        authToken={auth.user?.accessToken}
      >
        <SentryErrorBoundary
          fallback={errorProps => (
            <ErrorPage
              reportIssueButton={
                <SentryReportButton eventId={errorProps.eventId} />
              }
            />
          )}
          showDialog
          dialogOptions={{
            user: {
              name: auth.user?.claims.name,
              email: auth.user?.claims.email,
            },
          }}
        >
          <TopNavBar />

          <DocumentHandler>
            {isIdentified && (
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                <ModalsManager />

                <Routes>
                  <Route
                    element={
                      <>
                        <LocalSidebar />
                        <Outlet />
                      </>
                    }
                  >
                    <Route index element={<Navigate to="overview" />} />

                    <Route
                      path="overview"
                      element={
                        <Helm title="Overview">
                          <Overview />
                        </Helm>
                      }
                    />

                    <Route path="subscriptions">
                      <Route
                        index
                        element={
                          <Helm title="Subscriptions">
                            <Suspense>
                              <Subscriptions />
                            </Suspense>
                          </Helm>
                        }
                      />

                      <Route
                        path=":subscriptionId"
                        element={<Subscription />}
                      />
                    </Route>

                    <Route path="orders">
                      <Route
                        index
                        element={
                          <Helm title="Orders">
                            <Suspense>
                              <Orders />
                            </Suspense>
                          </Helm>
                        }
                      />

                      <Route path=":orderId" element={<Order />} />
                    </Route>

                    <Route path="destinations">
                      <Route
                        index
                        element={
                          <Suspense>
                            <Helm title="Destinations">
                              <Destinations />
                            </Helm>
                          </Suspense>
                        }
                      />

                      <Route
                        path="add"
                        element={<Navigate to="/destinations/new" />}
                      />

                      <Route path="new" element={<AddNewDestination />} />

                      <Route path=":destinationId" element={<Destination />} />
                    </Route>
                  </Route>

                  <Route
                    path="/*"
                    element={
                      <Suspense>
                        <MapViewer />
                      </Suspense>
                    }
                  >
                    {showCreateSubscription && (
                      <Route
                        path="subscriptions/new"
                        element={
                          <Helm title="Create Subscription">
                            <Suspense>
                              <Subscribe />
                            </Suspense>
                          </Helm>
                        }
                      />
                    )}

                    <Route
                      path="mosaics"
                      element={
                        <Suspense>
                          <Helm title="Basemaps">
                            <Mosaics />
                          </Helm>
                        </Suspense>
                      }
                    />

                    {showMapRoutes && (
                      <Route
                        path="explorer"
                        element={
                          <Suspense>
                            <Helm title="Daily results">
                              <Search />
                            </Helm>
                          </Suspense>
                        }
                      />
                    )}

                    {showMapRoutes && (
                      <Route
                        path="aois"
                        element={
                          <Suspense>
                            <Helm title="Areas of interest">
                              <AreasOfInterest />
                            </Helm>
                          </Suspense>
                        }
                      />
                    )}

                    {showMapRoutes && (
                      <Route
                        path="saved-searches"
                        element={
                          <Suspense>
                            <Helm title="Saved Searches">
                              <SavedSearches />
                            </Helm>
                          </Suspense>
                        }
                      />
                    )}

                    {showBrowser && (
                      <Route
                        path={`${ANALYZE_ROOT_ROUTE}/*`}
                        element={
                          <Suspense>
                            <Helm title="Browser">
                              <Analyze />
                            </Helm>
                          </Suspense>
                        }
                      />
                    )}
                  </Route>

                  <Route
                    path="*"
                    element={
                      <ErrorPage
                        message="the page was not found"
                        statusCode={404}
                      />
                    }
                  />
                </Routes>
              </Box>
            )}
          </DocumentHandler>

          <Notifier />
        </SentryErrorBoundary>
      </MapAuthProvider>
    </>
  );
};

export default App;
